import { formatISO } from "date-fns";
import {
    ACCOUNTING_REPORT_LABELS,
    AccountingReportType,
} from "@common/types/reports/report-type";
import { buildConsolidatedReportUrl } from "@components/ownership/ownershipConsolidationReports.helpers";
import { buildEliminationDetailUrl } from "../buildEliminationDetailUrl";
import { buildEliminationExceptionsUrl } from "../buildEliminationExceptionsUrl";
import { ConsolidationReportContext } from "./useConsolidationReportContext";

export type ConsolidationReportDestinationId =
    | "profitLoss"
    | "balanceSheet"
    | "eliminationLedger"
    | "exceptions";

export interface ConsolidationReportDestination {
    id: ConsolidationReportDestinationId;
    label: string;
    description: string;
    url: string;
}

const DESCRIPTIONS: Record<ConsolidationReportDestinationId, string> = {
    profitLoss: "Consolidated income and expenses over a selected period",
    balanceSheet:
        "Consolidated assets, liabilities, and equity at a selected date",
    eliminationLedger: "Consolidated intercompany elimination entries",
    exceptions: "Intercompany activity that still needs review",
};

/**
 * The single place that assembles the four supported consolidated report
 * destinations (labels + URLs) for a ready consolidation context. Every surface
 * — Reporting catalog, navigation, breadcrumbs — reads destinations from here so
 * labels and URL construction never drift between callers.
 */
export function getConsolidationReportDestinations({
    context,
    startDate,
    endDate,
}: {
    context: ConsolidationReportContext;
    startDate: Date;
    endDate: Date;
}): readonly ConsolidationReportDestination[] {
    const { group, parentEntity, ledgerId, workspaceId } = context;
    const startDateParam = formatISO(startDate, { representation: "date" });
    const endDateParam = formatISO(endDate, { representation: "date" });
    const eliminationParams = {
        startDate,
        endDate,
        workspaceId,
        consolidationGroupId: group.id,
    };

    return [
        {
            id: "profitLoss",
            label: ACCOUNTING_REPORT_LABELS[AccountingReportType.PROFIT_LOSS],
            description: DESCRIPTIONS.profitLoss,
            url: buildConsolidatedReportUrl({
                report: "profit-loss",
                parentEntity,
                ledgerId,
                group,
                dateRange: { startDate: startDateParam, endDate: endDateParam },
            }),
        },
        {
            id: "balanceSheet",
            label: ACCOUNTING_REPORT_LABELS[AccountingReportType.BALANCE_SHEET],
            description: DESCRIPTIONS.balanceSheet,
            url: buildConsolidatedReportUrl({
                report: "balance-sheet",
                parentEntity,
                ledgerId,
                group,
                dateRange: { startDate: startDateParam, endDate: endDateParam },
            }),
        },
        {
            id: "eliminationLedger",
            label: ACCOUNTING_REPORT_LABELS[
                AccountingReportType.ELIMINATION_DETAIL
            ],
            description: DESCRIPTIONS.eliminationLedger,
            url: buildEliminationDetailUrl(eliminationParams),
        },
        {
            id: "exceptions",
            label: ACCOUNTING_REPORT_LABELS[
                AccountingReportType.ELIMINATION_EXCEPTIONS
            ],
            description: DESCRIPTIONS.exceptions,
            url: buildEliminationExceptionsUrl(eliminationParams),
        },
    ];
}
